import { useQueryClient } from '@tanstack/react-query';
import { TimeSpan, BucketSize, Interval } from './api.types';
import { fetchVaultHistory } from './useQueryVaultHistory';
import { wait } from '@/lib/util';
import { QueryKeys } from './query-keys';
import { fetchTransactionStatus } from './usePollTransactionStatus';
import { fetchVaultList } from '../wallet/useQueryAccountVaultList';
import { fetchRuneUtxos } from '../wallet/useQueryRuneUtxos';

export const bucketSizeByTimeSpan: Record<TimeSpan, BucketSize> = {
  '1d': '1d',
  '1w': '1d',
  '1m': '1d',
  '1y': '1d',
};

const dayInSec = 24 * 60 * 60;

export const rangeByTimeSpan: Record<TimeSpan, number> = {
  '1d': dayInSec * 2,
  '1w': dayInSec * 7,
  '1m': dayInSec * 30,
  '1y': dayInSec * 365,
};

export const INTERVAL_TO_TIMESPAN: Record<Interval, TimeSpan> = {
  '24h': '1d',
  '7d': '1w',
  '30d': '1m',
  '1y': '1y',
};

const VAULT_TOKEN_MIN_DELAY = 5_000;
const POLLING_TIMEOUT_MS = 10 * 60_000; // 10 minutes

export async function post<TResult>(
  url: string,
  params: any
): Promise<TResult> {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    // Throw error with status for retry logic
    const error = new Error('Request failed');
    (error as any).status = res.status;
    (error as any).body = await res.text();
    throw error;
  }

  return await res.json();
}

export async function get<TResult>(url: string): Promise<TResult> {
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });
  return await res.json();
}

// Poll transaction status till its confirmed.
async function pollTransactionStatus(
  esploraUrl: string,
  txid: string,
  timeoutMs = POLLING_TIMEOUT_MS
) {
  console.log(`>>> [pollTransactionStatus] STARTED for ${txid}`);
  const start = Date.now();

  while (true) {
    try {
      const data = await fetchTransactionStatus(esploraUrl, txid);
      const isConfirmed = data?.status.confirmed;

      if (isConfirmed) {
        console.log(`>>> Transaction Confirmed. (0)`);
        return true;
      }
    } catch (e: any) {
      if (e.status === 404) {
        console.log(`tx not found yet `);
      } else {
        console.log(`tx status polling error: `, e);
      }
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error(`Timeout: Transaction ${txid} not found`);
    }

    await wait(3000);
  }
}

// Poll vault_history till the expected txid is received.
async function pollVaultHistory(
  queryClient: ReturnType<typeof useQueryClient>,
  vaultTxId: string,
  txids: string[],
  timeoutMs = POLLING_TIMEOUT_MS
) {
  const [txid, vault_txid] = txids;
  console.log(`>>> [pollVaultHistory] STARTED for ${txid} / ${vault_txid}`);
  const start = Date.now();

  while (true) {
    const data = await queryClient.fetchQuery({
      queryKey: [QueryKeys.vaultHistory, vaultTxId],
      queryFn: () => fetchVaultHistory(vaultTxId),
      staleTime: 0,
    });
    const transactionFound = data.some(
      (tx: any) =>
        tx.transaction_id === txid ||
        (vault_txid && tx.transaction_id === vault_txid)
    );

    if (transactionFound) {
      console.log(`>>> Transaction Found. (1) Vault History synced.`);
      return data.find((tx: any) => tx.transaction_id === txid);
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error(`Timeout: Transaction ${txid} not found`);
    }

    await wait(3000);
  }
}

// Poll ord server to check vault profile utxo response to match the latest txid.
export async function pollVaultToken(
  queryClient: ReturnType<typeof useQueryClient>,
  vaultPubkey: string,
  txids: string[],
  timeoutMs = POLLING_TIMEOUT_MS
) {
  const start = Date.now();
  const [txid, vault_txid] = txids;
  while (true) {
    const data = await queryClient.fetchQuery({
      // queryKey: [QueryKeys.vaultCtx, ordUrl],
      // queryFn: () => fetch_vault_profile(ordUrl, { vid: `${vaultTxId}i1` }),
      queryKey: [QueryKeys.accountVaultList, vaultPubkey],
      queryFn: () => fetchVaultList(vaultPubkey),
      staleTime: 0,
    });
    // const utxoTxId = data.utxo.txid;
    const resultUtxo = data?.vaults[0]?.utxo;
    const [utxoTxId, _] = resultUtxo.split(':');
    const transactionFound =
      utxoTxId === txid || (vault_txid && utxoTxId === vault_txid);
      utxoTxId === txid || (vault_txid && utxoTxId === vault_txid);

    if (transactionFound) {
      console.log(`>>> Transaction Found. (2) Vault Token data synced.`);
      return true;
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error(
        `Ord indexer timeout: utxo not found within ${timeoutMs / 1000} seconds.`
      );
    }

    await wait(5000);
  }
}

// Poll unit_utxo_by_address till the expected txid is received.
// useQueryRuneUtxos
async function pollRuneUtxos(
  queryClient: ReturnType<typeof useQueryClient>,
  runeAddress: string,
  txids: string[],
  timeoutMs = POLLING_TIMEOUT_MS
) {
  const [txid, vault_txid] = txids;
  console.log(`>>> [pollRuneUtxos] STARTED for txid=${txid} / runeAddress=${runeAddress}`);
  const start = Date.now();

  while (true) {
    const data = await queryClient.fetchQuery({
      queryKey: [QueryKeys.walletRunes, runeAddress],
      queryFn: () => fetchRuneUtxos(runeAddress),
      staleTime: 0,
    });
    const utxoFound = data.outputs.some(
      (utxo: any) => utxo.txid === txid
    );

    if (utxoFound) {
      const utxo = data.outputs.find((v: any) => v.txid === txid);
      console.log(`>>> Unit UTXO Found. (3) Vault data synced.`, utxo);
      return utxo;
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error(`Timeout: unit UTXO for ${txid} not found`);
    }

    await wait(3000);
  }
}

export async function syncUtxoSources(params: {
  queryClient: ReturnType<typeof useQueryClient>;
  vaultTxId: string;
  vaultPubkey: string;
  txids: string[];
  runeAddress?: string;
}) {
  const start = Date.now();
  const { queryClient, vaultTxId, vaultPubkey, txids, runeAddress } = params;

  await pollVaultHistory(queryClient, vaultTxId, txids);

  // Give multiple validators extra time to sync.
  await wait(5000);

  // Wait at least 10s before polling ord:
  const past = Date.now() - start;
  if (VAULT_TOKEN_MIN_DELAY - past > 0) {
    await wait(VAULT_TOKEN_MIN_DELAY - past);
  }

  await pollVaultToken(queryClient, vaultPubkey, txids);

  if (runeAddress) {
    return pollRuneUtxos(queryClient, runeAddress, txids);
  }

  return true;
}

export async function pollTxStatusAndSync(params: {
  queryClient: ReturnType<typeof useQueryClient>;
  esploraUrl: string;
  vaultPubkey: string;
  vaultTxId: string;
  txids: string[];
  runeAddress?: string;
}) {
  console.log(`>>> [pollTxStatusAndSync] ...`);
  await pollTransactionStatus(params.esploraUrl, params.txids[0]);

  if (params.vaultTxId) {
    console.log(`>>> [syncUtxoSources] vaultTxId=${params.vaultTxId} ...`);
    return syncUtxoSources(params);
  } else {
    console.log(`>>> [NO syncUtxoSources] missing vaultTxId.`);
    return true;
  }
}
